/**
 *
 * Sidebar
 *
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, matchPath, useHistory, useLocation } from 'react-router';
import {
  BarChartOutlined,
  OrderedListOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';

import * as S from './Sidebar.styles';

import { selectSidebarCollapsed } from '../../../styles/theme/slice/selectors';

import MapIcon from './icons/MapIcon';
import PlantIcon from './icons/PlantIcon';
import BusinessIcon from './icons/BusinessIcon';
import MeteoIcon from './icons/MeteoIcon';
import ResourcesIcon from './icons/ResourcesIcon';
import { RouteConstants } from '../../routes';
import { selectSelectedEnv } from 'app/containers/HiikoEnv/slice/selectors';
import { selectTaskListFilters } from 'app/containers/Task/slice/selectors';
import { getTaskListFiltersSearchQuery } from 'utils/helpers';
import {
  selectIsConsultant,
  selectPermissions,
  selectPermissionsByCompany,
} from 'app/containers/Authorization/slice/selectors';
import FieldResourcesIcon from 'app/containers/Task/icons/FieldResourcesIcon';
import RecipesIcon from './icons/RecipesIcon';
import ToggleSidebarButton from './ToggleSidebarButton';
import { useResponsiveQuery } from '../../../utils/responsiveQuery/useResponsiveQuery';
import { useThemeSlice } from 'styles/theme/slice';

interface IProps {}

interface IMenuItem {
  id: string;
  title: string;
  icon: () => JSX.Element;
  path?: string;
  linkTo?: string;
  disabled: boolean;
  submenuItems?: {
    id: string;
    title: string;
    path: string;
    linkTo: string;
    typeId: number;
    disabled: boolean;
  }[];
}

export function Sidebar(props: IProps) {
  const { t } = useTranslation();

  const isCollapsed = useSelector(selectSidebarCollapsed);
  const env = useSelector(selectSelectedEnv);
  const taskListFiltersObj = useSelector(selectTaskListFilters);
  const isConsultant = useSelector(selectIsConsultant);

  const { mobile } = useResponsiveQuery();
  const themeActions = useThemeSlice().actions;
  const dispatch = useDispatch();

  const {
    usersAdministration,
    companyAdministration,
    cultureAdministration,
    resourceAdministration,
    taskAdministration,
    fieldAdministration,
    garageAdministration,
  } = useSelector(selectPermissions);

  const { analytics: companiesWithAnalyticsPermission } = useSelector(
    selectPermissionsByCompany,
  );

  const taskListFiltersSearchQuery = useMemo(
    () => getTaskListFiltersSearchQuery({ filters: taskListFiltersObj }),
    [taskListFiltersObj],
  );

  const menuItems: IMenuItem[] = useMemo(() => {
    if (env?.id) {
      const items = [
        {
          title: t('Sidebar.Fields', 'Fields'),
          id: 'sidebarFields',
          icon: () => <MapIcon />,
          path: RouteConstants.field.index,
          linkTo: generatePath(RouteConstants.field.list, {
            envId: env.id,
          }),
          disabled: !fieldAdministration,
        },
        {
          title: t('Sidebar.Tasks', 'Tasks'),
          id: 'sidebarTasks',
          icon: () => <OrderedListOutlined />,
          path: RouteConstants.task.index,
          linkTo: generatePath(RouteConstants.task.list, {
            envId: env.id,
          }).concat(`?${taskListFiltersSearchQuery}`),
          disabled: !taskAdministration,
        },
        ...(isConsultant
          ? [
              {
                title: t('Sidebar.Recipes', 'Recipes'),
                id: 'sidebarRecipes',
                icon: () => <RecipesIcon />,
                path: RouteConstants.recipe.index,
                linkTo: generatePath(RouteConstants.recipe.index, {
                  envId: env.id,
                }),
                disabled: !taskAdministration,
              },
            ]
          : []),

        {
          title: t('Sidebar.UsedResources', 'Used resources'),
          id: 'sidebarUsedResources',
          icon: () => <ResourcesIcon />,
          path: RouteConstants.usedResource.index,
          disabled: false,
          linkTo: generatePath(RouteConstants.usedResource.index, {
            envId: env.id,
          }),
        },
        {
          title: t('Sidebar.Warehouse', 'Warehouse'),
          id: 'sidebarWarehouse',
          icon: () => <ResourcesIcon />,
          path: RouteConstants.resource.index,
          disabled: !resourceAdministration,
          submenuItems: [
            {
              title: t('Sidebar.Harvest', 'Harvest'),
              id: 'sidebarHarvest',
              typeId: 0,
              path: RouteConstants.resource.harvest,
              linkTo: generatePath(RouteConstants.resource.harvest, {
                envId: env.id,
              }),
              disabled: !resourceAdministration,
            },

            {
              title: t('Sidebar.ResourceAllocation', 'Resource allocation'),
              id: 'sidebarResourceAllocation',
              typeId: 0,
              path: RouteConstants.resource.resourceAllocation,
              linkTo: generatePath(RouteConstants.resource.resourceAllocation, {
                envId: env.id,
              }),
              disabled: !resourceAdministration,
            },
          ],
        },
        {
          title: t('Sidebar.Cultures', 'Cultures'),
          id: 'sidebarCultures',
          icon: () => <PlantIcon />,
          path: RouteConstants.cultures.index,
          linkTo: generatePath(RouteConstants.cultures.list, {
            envId: env.id,
          }),
          disabled: !cultureAdministration,
        },
        {
          title: t('Sidebar.Companies', 'Companies'),
          id: 'sidebarCompanies',
          icon: () => <BusinessIcon />,
          path: RouteConstants.company.index,
          linkTo: generatePath(RouteConstants.company.list, {
            envId: env.id,
          }),
          disabled: !companyAdministration,
        },
        {
          title: t('Sidebar.Inventory', 'Inventory'),
          id: 'sidebarInventory',
          icon: () => <FieldResourcesIcon />,
          path: RouteConstants.garage.index,
          linkTo: generatePath(RouteConstants.garage.index, {
            envId: env.id,
          }),
          disabled: !garageAdministration,
        },
        {
          title: t('Sidebar.Meteorology', 'Meteorology'),
          id: 'sidebarMeteorology',
          icon: () => <MeteoIcon />,
          path: RouteConstants.meteo.index,
          linkTo: generatePath(RouteConstants.meteo.forecast, {
            envId: env.id,
          }),
          disabled: false,
        },
        {
          title: t('Sidebar.Users', 'Users'),
          id: 'sidebarUsers',
          icon: () => <UserOutlined />,
          path: RouteConstants.user.index,
          linkTo: generatePath(RouteConstants.user.list, {
            envId: env.id,
          }),
          disabled: !usersAdministration,
        },
        {
          title: t('Sidebar.Settings', 'Settings'),
          id: 'sidebarSettings',
          icon: () => <SettingOutlined />,
          path: RouteConstants.settings.index,
          linkTo: generatePath(RouteConstants.settings.index, {
            envId: env.id,
          }),
          disabled: false,
        },
        {
          title: t('Sidebar.Analytics', 'Analytics'),
          id: 'sidebarAnalytics',
          icon: () => <BarChartOutlined />,
          path: RouteConstants.analytics.index,
          linkTo: generatePath(RouteConstants.analytics.index, {
            envId: env.id,
          }),
          disabled: !companiesWithAnalyticsPermission.length,
        },
      ];

      return items;
    } else {
      return [];
    }
  }, [
    t,
    env,
    taskListFiltersSearchQuery,
    usersAdministration,
    companyAdministration,
    cultureAdministration,
    resourceAdministration,
    taskAdministration,
    fieldAdministration,
    garageAdministration,
    companiesWithAnalyticsPermission,
    isConsultant,
  ]);

  const location = useLocation();
  const history = useHistory();

  const [selectedKey, setSelectedKey] = useState(
    menuItems.find(
      menuItem => menuItem.path && matchPath(location.pathname, menuItem.path),
    )?.title || '',
  );

  const onMenuItemClick = (linkTo?: string) => {
    if (mobile) {
      dispatch(themeActions.toggleSidebar());
    }

    if (linkTo) {
      history.push(linkTo);
    }
  };

  useEffect(() => {
    let key = '';

    menuItems.forEach(menuItem => {
      if (menuItem.submenuItems?.length) {
        menuItem.submenuItems.forEach(submenuItem => {
          if (
            matchPath(location.pathname, {
              path: submenuItem.path,
            })
          ) {
            if (
              submenuItem.path === RouteConstants.resource.resourceAllocation ||
              submenuItem.path === RouteConstants.resource.harvest
            ) {
              key = submenuItem.title;
            } else {
              const pathnameParts = location.pathname.split('resource-type')[1];
              const typeId = pathnameParts.split('/')[1];
              if (Number(typeId) === submenuItem.typeId) {
                key = submenuItem.title;
              }
            }
          }
        });
      } else if (menuItem.path && matchPath(location.pathname, menuItem.path)) {
        key = menuItem.title;
      }
    });

    setSelectedKey(key);
  }, [location, menuItems]);

  const mobileSidebarProps = {
    collapsedWidth: 0,
    width: '100%',
  };

  return (
    <S.Sidebar collapsed={isCollapsed} {...(mobile && mobileSidebarProps)}>
      <S.SidebarInner>
        <Row
          wrap={false}
          justify="space-between"
          align="middle"
          style={{ padding: '0 24px' }}
        >
          <S.LogoContainer
            $collapsed={isCollapsed}
            onClick={() => {
              if (env?.id) {
                history.push(
                  generatePath(RouteConstants.field.list, {
                    envId: env.id,
                  }),
                );
              }
            }}
          >
            <S.Logo />
            <S.LogoText $collapsed={isCollapsed} />
          </S.LogoContainer>
          {mobile && <ToggleSidebarButton />}
        </Row>

        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          style={{
            maxHeight: 'calc(100% - 64px)',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {menuItems.map(item =>
            Boolean(item.submenuItems?.length) ? (
              <Menu.SubMenu
                icon={item.icon()}
                key={item.title}
                title={item.title}
                disabled={item.disabled}
              >
                {item.submenuItems?.map(submenuItem => (
                  <Menu.Item
                    key={submenuItem.title}
                    onClick={() => onMenuItemClick(submenuItem.linkTo)}
                    disabled={submenuItem.disabled}
                    id={submenuItem.id}
                  >
                    {submenuItem.title}
                  </Menu.Item>
                ))}
              </Menu.SubMenu>
            ) : (
              <Menu.Item
                icon={item.icon()}
                key={item.title}
                disabled={!item.linkTo || item.disabled}
                onClick={() => onMenuItemClick(item.linkTo)}
                id={item.id}
              >
                {item.title}
              </Menu.Item>
            ),
          )}
        </Menu>
      </S.SidebarInner>
    </S.Sidebar>
  );
}
